import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Alert,
  Pagination,
  CircularProgress,
  InputAdornment,
  Container,
} from "@mui/material";
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Inventory,
  LocationOn,
  Storage,
  Search,
  FilterList,
} from "@mui/icons-material";
import { racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvRacks = () => {
  const navigate = useNavigate();
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRack, setSelectedRack] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRacks();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm, zoneFilter, statusFilter]);

  const fetchRacks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(zoneFilter !== "all" && { zone: zoneFilter }),
        ...(statusFilter !== "all" && { isActive: statusFilter }),
      };

      const response = await racksAPI.getAll(params);
      const { racks: racksData, pagination: paginationData } = response.data.data;

      setRacks(racksData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      console.error("Error fetching racks:", error);
      enqueueSnackbar("Error fetching racks", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleZoneFilter = (value) => {
    setZoneFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, rack) => {
    setAnchorEl(event.currentTarget);
    setSelectedRack(rack);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    if (selectedRack) {
      navigate(`/hospital/hospital-inventory/racks/${selectedRack._id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedRack) {
      navigate(`/hospital/hospital-inventory/racks/${selectedRack._id}/edit`);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const deleteRack = async () => {
    if (!selectedRack) return;

    try {
      await racksAPI.delete(selectedRack._id);
      enqueueSnackbar("Rack deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setSelectedRack(null);
      fetchRacks();
    } catch (error) {
      console.error("Delete rack error:", error);
      const errorMessage =
        error.response?.data?.message || "Error deleting rack";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const getZoneColor = (zone) => {
    const colors = {
      general: "primary",
      cold_storage: "info",
      controlled: "warning",
      hazardous: "error",
      quarantine: "secondary",
    };
    return colors[zone] || "default";
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return "error";
    if (utilization >= 75) return "warning";
    return "success";
  };

  const formatZoneName = (zone) => {
    return zone
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
          }}
        >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box>
        {/* Header Section */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: 3
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  📦 Rack Management
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Manage and organize your inventory storage racks
                </Typography>
              </Box>
              {hasAnyRole(["admin", "inventory_manager"]) && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate("/hospital/hospital-inventory/racks/new")}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontSize: '1.1rem',
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Add New Rack
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FilterList sx={{ mr: 2, fontSize: '2rem', color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                Filters & Search
              </Typography>
            </Box>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search racks by name, description, or location..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1.5rem' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      padding: '12px 14px',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Storage Zone</InputLabel>
                  <Select
                    value={zoneFilter}
                    label="Storage Zone"
                    onChange={(e) => handleZoneFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem', padding: '12px 14px' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Zones</MenuItem>
                    <MenuItem value="general" sx={{ fontSize: '1.1rem' }}>General</MenuItem>
                    <MenuItem value="cold_storage" sx={{ fontSize: '1.1rem' }}>Cold Storage</MenuItem>
                    <MenuItem value="controlled" sx={{ fontSize: '1.1rem' }}>Controlled</MenuItem>
                    <MenuItem value="hazardous" sx={{ fontSize: '1.1rem' }}>Hazardous</MenuItem>
                    <MenuItem value="quarantine" sx={{ fontSize: '1.1rem' }}>Quarantine</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem', padding: '12px 14px' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Status</MenuItem>
                    <MenuItem value="true" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                    <MenuItem value="false" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2, 
                    bgcolor: 'primary.light', 
                    borderRadius: 2,
                    color: 'white'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {pagination.total}
                  </Typography>
                  <Typography variant="body1">
                    Total Racks
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Racks Table Section */}
        <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              📋 Racks Overview
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <TableContainer 
                  component={Paper} 
                  variant="outlined"
                  sx={{ borderRadius: 2, border: '2px solid', borderColor: 'grey.200' }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Rack Name
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Location
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Zone
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Capacity
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Utilization
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Status
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {racks.map((rack) => {
                        const utilization =
                          rack.capacity > 0
                            ? (rack.currentOccupancy / rack.capacity) * 100
                            : 0;

                        return (
                          <TableRow 
                            key={rack._id} 
                            hover 
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'action.hover',
                                transform: 'scale(1.01)',
                                transition: 'all 0.2s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell sx={{ py: 3 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {rack.name}
                              </Typography>
                              {rack.description && (
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  sx={{ fontSize: '1rem', mt: 0.5 }}
                                >
                                  {rack.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 3 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <LocationOn sx={{ fontSize: '1.5rem' }} color="primary" />
                                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                                  {[
                                    rack.location.aisle,
                                    rack.location.row,
                                    rack.location.section,
                                  ]
                                    .filter(Boolean)
                                    .join(" - ") || "Not specified"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 3 }}>
                              <Chip
                                label={formatZoneName(rack.location.zone)}
                                color={getZoneColor(rack.location.zone)}
                                size="medium"
                                sx={{ 
                                  fontSize: '1rem', 
                                  fontWeight: 'bold',
                                  px: 1,
                                  height: '32px'
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 3 }}>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                {rack.currentOccupancy} / {rack.capacity}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontSize: '1rem' }}
                              >
                                Available: {rack.capacity - rack.currentOccupancy}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 3 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(utilization, 100)}
                                  color={getUtilizationColor(utilization)}
                                  sx={{ 
                                    flexGrow: 1, 
                                    height: 12, 
                                    borderRadius: 6,
                                    bgcolor: 'grey.200'
                                  }}
                                />
                                <Typography variant="body1" sx={{ 
                                  minWidth: 50, 
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  color: getUtilizationColor(utilization) === 'error' ? 'error.main' : 
                                         getUtilizationColor(utilization) === 'warning' ? 'warning.main' : 'success.main'
                                }}>
                                  {utilization.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 3 }}>
                              <Chip
                                label={rack.isActive ? "Active" : "Inactive"}
                                color={rack.isActive ? "success" : "default"}
                                size="medium"
                                sx={{ 
                                  fontSize: '1rem', 
                                  fontWeight: 'bold',
                                  px: 1,
                                  height: '32px'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 3 }}>
                              <IconButton
                                size="large"
                                onClick={(e) => handleMenuOpen(e, rack)}
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'primary.dark',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <MoreVert sx={{ fontSize: '1.5rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {racks.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Storage
                      sx={{ fontSize: 80, color: "text.secondary", mb: 3 }}
                    />
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                      No racks found
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 4 }}
                    >
                      {searchTerm ||
                      zoneFilter !== "all" ||
                      statusFilter !== "all"
                        ? "Try adjusting your search filters"
                        : "Get started by creating your first rack"}
                    </Typography>
                    {hasAnyRole(["admin", "inventory_manager"]) && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/hospital/hospital-inventory/racks/new")}
                        sx={{ 
                          fontSize: '1.1rem',
                          px: 4,
                          py: 1.5
                        }}
                      >
                        Create First Rack
                      </Button>
                    )}
                  </Box>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Pagination
                      count={pagination.pages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '1.1rem',
                          margin: '0 4px',
                        },
                        '& .MuiPaginationItem-page.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { 
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 200
            }
          }}
        >
          <MenuItem onClick={handleView} sx={{ py: 1.5 }}>
            <Visibility sx={{ mr: 2, fontSize: '1.5rem' }} />
            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
              View Details
            </Typography>
          </MenuItem>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem onClick={handleEdit} sx={{ py: 1.5 }}>
              <Edit sx={{ mr: 2, fontSize: '1.5rem' }} />
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                Edit Rack
              </Typography>
            </MenuItem>
          )}
          {hasAnyRole(["admin"]) && (
            <MenuItem
              onClick={handleDelete}
              disabled={selectedRack?.currentOccupancy > 0}
              sx={{ 
                py: 1.5,
                color: selectedRack?.currentOccupancy > 0 ? 'grey.500' : 'error.main'
              }}
            >
              <Delete sx={{ mr: 2, fontSize: '1.5rem' }} />
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                Delete Rack
              </Typography>
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'error.main', 
            color: 'white',
            fontSize: '1.3rem',
            fontWeight: 'bold'
          }}>
            🗑️ Delete Rack
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem' }}>
              ⚠️ This action cannot be undone.
            </Alert>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.2rem' }}>
              Are you sure you want to delete rack{" "}
              <strong style={{ color: 'error.main' }}>{selectedRack?.name}</strong>?
            </Typography>
            {selectedRack?.currentOccupancy > 0 && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '1.1rem' }}>
                ❌ Cannot delete rack that contains stock. Transfer or remove stock first.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{ 
                fontSize: '1.1rem',
                px: 3,
                py: 1
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteRack}
              variant="contained"
              color="error"
              disabled={selectedRack?.currentOccupancy > 0}
              sx={{ 
                fontSize: '1.1rem',
                px: 3,
                py: 1,
                fontWeight: 'bold'
              }}
            >
              Delete Rack
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
    </Box>
  );
};